// Undo/redo history. Legacy: snapshot L5727-5799.
// Fabric v7 changes (map §4.2, §4.4):
//   - serialize with canvas.toObject(props) — toJSON() no longer takes the props arg
//   - loadFromJSON returns a Promise (no callback); isUndoRedo reset moves into .then()
import type { Canvas, FabricObject } from 'fabric'
import { getCanvas, state } from './state'
import { refreshGrid } from './canvas'

// Custom Fabric properties that must survive serialization. Ported from the
// legacy `canvasCustomProps` (L5729) PLUS `_strokeStyle` / `_cornerRadius` — which
// were MISSING in the legacy allowlist (map §3.1 / §4.4, a latent save/load bug we fix).
export const CANVAS_CUSTOM_PROPS = [
  'id',
  'barcodeFormat', 'barcodeValue',
  'barcodeStyleFont', 'barcodeStyleTextPos', 'barcodeStyleFontSize', 'barcodeStyleShowText',
  'qrValue',
  'autoFitWidth', '_autoFitOrigSize',
  'isBgImage',
  '_strokeStyle', '_cornerRadius',
  'olmDataRegion', 'olmDataRegionLabel', // AI-import data-table flags (until converted)
  // (Tables serialize via the Table extension's own toObject/fromObject + classRegistry.)
]

const MAX_UNDO = 40

/** Serialize the canvas, excluding transient grid objects (legacy getCanvasJsonWithOriginals). */
export function serializeCanvas(canvas: Canvas = getCanvas()): string {
  // Filter the grid overlay out of the JSON — NOT by removing/re-adding the
  // objects: a physical re-add fires object:added → saveState → back here,
  // which recursed infinitely once the grid overlay actually existed.
  // NOTE: batch live-preview text restoration (legacy L5737-5751) is added in Phase 3.
  const json = canvas.toObject([...CANVAS_CUSTOM_PROPS, 'isGrid']) as { objects?: { isGrid?: boolean }[] }
  json.objects = (json.objects ?? []).filter((o) => !o.isGrid)
  return JSON.stringify(json)
}

/** Push the current canvas state (legacy saveState). No-op while replaying undo/redo. */
export function saveState(): void {
  if (state.isUndoRedo) return
  state.undoStack.push(serializeCanvas())
  if (state.undoStack.length > MAX_UNDO) state.undoStack.shift()
  state.redoStack = []
}

async function restore(json: string): Promise<void> {
  const canvas = getCanvas()
  const prevBg = canvas.backgroundColor
  await canvas.loadFromJSON(json)
  // loadFromJSON sets backgroundColor to undefined when a state has no `background` key —
  // keep the page background instead of dropping to the transparent grid.
  if (!canvas.backgroundColor && prevBg) canvas.backgroundColor = prevBg
  refreshGrid() // loadFromJSON wiped the grid overlay (grid lines are canvas objects)
  canvas.requestRenderAll()
  // Phase 2+: updateLayers() / findBgImageObj(); Phase 1 has no layers panel yet.
  state.isUndoRedo = false
}

/** Reseed the undo baseline to the current canvas (e.g. right after loading a design), so
 * undo can't jump back to the pre-load empty canvas and the baseline carries its background. */
export function resetHistory(): void {
  state.undoStack = [serializeCanvas()]
  state.redoStack = []
}

export async function undo(): Promise<void> {
  if (state.undoStack.length <= 1) return
  state.isUndoRedo = true
  state.redoStack.push(state.undoStack.pop()!)
  await restore(state.undoStack[state.undoStack.length - 1])
}

export async function redo(): Promise<void> {
  if (state.redoStack.length === 0) return
  state.isUndoRedo = true
  const json = state.redoStack.pop()!
  state.undoStack.push(json)
  await restore(json)
}

/** Wire auto-save on canvas mutations and seed the baseline state. */
export function initHistory(): void {
  const canvas = getCanvas()
  // Grid-overlay churn is not a document change — never push undo states for it.
  const onChange = (opt?: { target?: FabricObject & { isGrid?: boolean } }) => {
    if (opt?.target?.isGrid) return
    saveState()
  }
  canvas.on('object:added', onChange)
  canvas.on('object:removed', onChange)
  canvas.on('object:modified', onChange)
  state.undoStack = [serializeCanvas(canvas)] // baseline so the first edit can undo back
  state.redoStack = []
}
